import {
  CustomFieldOption,
  CustomFieldOptionField,
} from "../types/client.type";
import { projectTypes } from "./opcoesBriefingInicial";

export interface EventFieldOption {
  value: string;
  label: string;
}

/** Campos que aceitam opções personalizadas, na ordem exibida na aba. */
export const CUSTOM_OPTION_FIELDS: {
  field: CustomFieldOptionField;
  label: string;
}[] = [
  { field: CustomFieldOptionField.EVENT_TYPE, label: "Tipo do Evento" },
  { field: CustomFieldOptionField.EVENT_FORMAT, label: "Formato do Evento" },
];

/**
 * Opções padrão de cada campo. O Formato grava códigos (o relatório da Api os
 * traduz); as opções personalizadas gravam o próprio texto.
 */
export const DEFAULT_EVENT_FIELD_OPTIONS: Record<
  CustomFieldOptionField,
  EventFieldOption[]
> = {
  [CustomFieldOptionField.EVENT_TYPE]: projectTypes.map((type) => ({
    value: type,
    label: type,
  })),
  [CustomFieldOptionField.EVENT_FORMAT]: [
    { label: "Presencial", value: "presential" },
    { label: "Virtual", value: "online" },
    { label: "Híbrido", value: "hibrid" },
  ],
};

export const DUPLICATE_OPTION_MESSAGE = "Esta opção já existe para este campo";

/** Mesma comparação do Benner: ignora caixa, acentos e espaços nas pontas. */
export function normalizeOption(option: string): string {
  return option
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

const compareLabels = (a: string, b: string) =>
  a.localeCompare(b, "pt-BR", { sensitivity: "base" });

export function sortOptionLabels(labels: string[]): string[] {
  return [...labels].sort(compareLabels);
}

export function getCustomOptions(
  customFieldOptions: CustomFieldOption[] | undefined,
  field: CustomFieldOptionField
): string[] {
  return customFieldOptions?.find((c) => c.field === field)?.options ?? [];
}

/** Troca as opções de um campo; campo sem opções sai da lista. */
export function setCustomOptions(
  customFieldOptions: CustomFieldOption[] | undefined,
  field: CustomFieldOptionField,
  options: string[]
): CustomFieldOption[] {
  const others = (customFieldOptions ?? []).filter((c) => c.field !== field);
  return options.length ? [...others, { field, options }] : others;
}

/** A opção já existe entre as padrão ou as personalizadas do campo? */
export function isDuplicateOption(
  field: CustomFieldOptionField,
  customOptions: string[],
  option: string
): boolean {
  const normalized = normalizeOption(option);
  return [
    ...DEFAULT_EVENT_FIELD_OPTIONS[field].map((o) => o.label),
    ...customOptions,
  ].some((existing) => normalizeOption(existing) === normalized);
}

/**
 * Opções padrão + personalizadas do cliente, em ordem alfabética. O valor já
 * gravado no projeto continua disponível mesmo que a opção personalizada
 * tenha sido removida do cliente.
 */
export function getEventFieldOptions(
  field: CustomFieldOptionField,
  customFieldOptions?: CustomFieldOption[],
  currentValue?: string
): EventFieldOption[] {
  const options: EventFieldOption[] = [
    ...DEFAULT_EVENT_FIELD_OPTIONS[field],
    ...getCustomOptions(customFieldOptions, field).map((o) => ({
      value: o,
      label: o,
    })),
  ];
  if (currentValue && !options.some((o) => o.value === currentValue)) {
    options.push({ value: currentValue, label: currentValue });
  }
  return options.sort((a, b) => compareLabels(a.label, b.label));
}
